import {
  Button,
  Icon,
  Input,
  Modal,
  Toast,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { machineAccounts, type MachineAccount } from "../../api/machineAccounts.service";
import { PageHeader } from "../../components/PageHeader";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import PermissionGate from "../../components/Permissions/PermissionGate";
import { ROUTES } from "../../routes";
import styles from "./MachineAccounts.module.css";

function MachineAccountsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [_authCheckLoading, setAuthCheckLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [newAccountName, setNewAccountName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    async function checkAuth() {
      try {
        await machineAccounts.getAll({ limit: 1, offset: 0 });
        setAuthCheckLoading(false);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to check auth");
        const status = (error as Error & { status?: number }).status;
        const isAuthError = status === 401 || status === 403;
        if (isAuthError) {
          setAuthError(error);
        }
        setAuthCheckLoading(false);
      }
    }
    checkAuth();
  }, []);

  function handleOpenCreateModal() {
    setNewAccountName("");
    setCreateError(null);
    setIsCreateModalOpen(true);
  }

  function handleCloseCreateModal() {
    setIsCreateModalOpen(false);
    setNewAccountName("");
    setCreateError(null);
  }

  async function handleCreateAccount() {
    if (!newAccountName.trim()) {
      setCreateError("Name is required");
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    try {
      const account: MachineAccount = await machineAccounts.create({ name: newAccountName.trim() });
      setIsCreateModalOpen(false);
      setNewAccountName("");
      setToast({
        message: `Machine account "${account.name}" created successfully`,
        type: "success",
      });

      const isOnDetailPage = location.pathname !== ROUTES.machineAccounts.list;
      if (isOnDetailPage) {
        navigate(ROUTES.machineAccounts.detail(account.id));
      } else {
        setRefetchTrigger(prev => prev + 1);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to create machine account");
      setCreateError(error.message);
    } finally {
      setIsCreating(false);
    }
  }

  function handleCloseToast() {
    setToast(null);
  }

  function handleMutationSuccess() {
    setRefetchTrigger(prev => prev + 1);
  }

  function handleBackClick() {
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
    } else {
      navigate(ROUTES.machineAccounts.list);
    }
  }

  const isDetailPage = location.pathname !== ROUTES.machineAccounts.list;

  if (authError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="Machine Accounts"
          subtitle="Manage service-to-service authentication accounts"
        >
          <div style={{ display: "flex", gap: "12px" }}>
            {isDetailPage && (
              <Button variant="outlined" color="default" onClick={handleBackClick}>
                <Icon icon="arrow-left" size="small" />
                Back
              </Button>
            )}
            <PermissionGate permission="platform.write">
              <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>
                Create Machine Account
              </Button>
            </PermissionGate>
          </div>
        </PageHeader>
      </div>

      <Outlet
        context={{
          toast,
          setToast,
          refetchTrigger,
          onMutationSuccess: handleMutationSuccess,
        }}
      />

      {isCreateModalOpen && (
        <Modal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          title="Create Machine Account"
          size="small"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="machine-account-name">
                Name
              </label>
              <Input
                id="machine-account-name"
                value={newAccountName}
                onChange={e => setNewAccountName(e.target.value)}
                placeholder="e.g. data-pipeline-service"
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleCreateAccount();
                  }
                }}
              />
              {createError && (
                <span style={{ color: "var(--color-error)", fontSize: "var(--font-size-sm)" }}>
                  {createError}
                </span>
              )}
            </div>
            <div className={styles.formActions}>
              <Button
                variant="outlined"
                color="default"
                onClick={handleCloseCreateModal}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateAccount}
                disabled={isCreating}
              >
                {isCreating ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
          duration={5000}
        />
      )}
    </div>
  );
}

export default MachineAccountsLayout;
